import React, { useState, useEffect } from 'react';
import { InstagramIcon, FacebookIcon, YouTubeIcon } from '../components/SocialIcons';
import './hotels.css';
import { buildApiUrl } from '../utils/apiUrl';
import trackVisitor from '../utils/visitorTracker';

const placeholderImg = '/defaultimages/imagenotavailable.jpg';

// Resolve image paths coming from DB or local data. The data may contain paths like
// './hotelsimages/xyz.jpg' (from original require() replacement) or already absolute URLs.
const resolveImageUrl = (p) => {
  if (!p) return placeholderImg;
  if (typeof p !== 'string') return placeholderImg;
  // If it's already an absolute URL, return as-is
  if (p.startsWith('http://') || p.startsWith('https://') || p.startsWith('/')) return p;
  // remove leading ./ if present
  let s = p.replace(/^\.\//, '');
  // if path already begins with 'images/', keep it, otherwise assume it's inside 'hotelsimages' folder
  if (s.startsWith('images/')) return `/${s}`;
  // common source in our data is 'hotelsimages/...'
  if (s.startsWith('hotelsimages/')) return `/images/${s}`;
  // fallback: prefix with /images/
  return `/images/${s}`;
};

const Hotels = () => {
  const [search, setSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [location, setLocation] = useState('');
  const [modalHotel, setModalHotel] = useState(null);
  const [businesstype, setBusinesstype] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [hoverPrev, setHoverPrev] = useState(false);
  const [hoverNext, setHoverNext] = useState(false);
  const [hoverClose, setHoverClose] = useState(false);
  const [hotelsList, setHotelsList] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const hotelsArray = Array.isArray(hotelsList) ? hotelsList : [];



  // Get unique locations for dropdown (uses new `locationname` field)
  const locationOptions = Array.from(new Set(hotelsArray.map(h => h.locationname))).sort();
  // Get unique business types for dropdown (uses `businesstype` field)
  const businesstypeOptions = Array.from(new Set(hotelsArray.map(h => h.businesstype).filter(Boolean))).sort();

  // Get name suggestions based on current search
  const nameSuggestions = search.length > 0
    ? hotelsArray
        .filter(h => h.businessname && h.businessname.toLowerCase().includes(search.toLowerCase()))
        .map(h => h.businessname)
        .slice(0, 5)
    : [];

  // Fetch hotels from backend on mount
  useEffect(() => {
    let mounted = true;

    fetch(buildApiUrl('/api/hotels'))
      .then(r => r.json())
      .then(data => {
        if (!mounted) return;
        const normalizedData = Array.isArray(data)
          ? data
          : (Array.isArray(data?.data) ? data.data : (Array.isArray(data?.rows) ? data.rows : []));
        setHotelsList(normalizedData);
      })
      .catch(err => console.error('Failed to load hotels from API', err));
    return () => { mounted = false; };
  }, []);

  // detect mobile viewport and update on resize
  useEffect(() => {
    const onResize = () => setIsMobile(typeof window !== 'undefined' && window.innerWidth <= 600);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Filtered hotels, sorted by listorder/listingorder
  let filteredHotels = hotelsArray
    .filter(hotel =>
      (location === '' || hotel.locationname === location) &&
      (businesstype === '' || hotel.businesstype === businesstype) &&
      (search.trim() === '' || (hotel.businessname && hotel.businessname.toLowerCase().includes(search.trim().toLowerCase())))
    )
    .sort((a, b) => {
      const la = (typeof a.listorder === 'number' ? a.listorder : (typeof a.listingorder === 'number' ? a.listingorder : Number.MAX_SAFE_INTEGER));
      const lb = (typeof b.listorder === 'number' ? b.listorder : (typeof b.listingorder === 'number' ? b.listingorder : Number.MAX_SAFE_INTEGER));
      if (la !== lb) return la - lb;
      const da = a.dateposted ? new Date(a.dateposted).getTime() : 0;
      const db = b.dateposted ? new Date(b.dateposted).getTime() : 0;
      return da - db;
    });

  const handleShareClick = (hotel) => {
  const shareText = `${hotel.businessname} - ${hotel.locationname}\n${hotel.aboutdescription}`;
  const shareUrl = hotel.urllink || window.location.href;
    
    if (navigator.share) {
      navigator.share({
        title: hotel.businessname,
        text: shareText,
        url: shareUrl
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    }
  };

  // When modal opens, set selected photo to the first available photo
  useEffect(() => {
    if (modalHotel) {
      const photos = (modalHotel.photos && modalHotel.photos.length > 0)
        ? modalHotel.photos
        : [];
      setSelectedPhoto(photos[0] || null);
    } else {
      setSelectedPhoto(null);
    }
  }, [modalHotel]);

  // Keyboard navigation for modal (left/right arrow to change photos)
  useEffect(() => {
    if (!modalHotel) return;
    const photos = (modalHotel.photos && modalHotel.photos.length > 0)
      ? modalHotel.photos
      : [modalHotel.imagepath];
    const handler = (e) => {
      if (!modalHotel) return;
      if (e.key === 'ArrowLeft') {
        const idx = photos.findIndex(p => p === selectedPhoto);
        const current = idx === -1 ? 0 : idx;
        const prevIndex = (current - 1 + photos.length) % photos.length;
        setSelectedPhoto(photos[prevIndex]);
      } else if (e.key === 'ArrowRight') {
        const idx = photos.findIndex(p => p === selectedPhoto);
        const current = idx === -1 ? 0 : idx;
        const nextIndex = (current + 1) % photos.length;
        setSelectedPhoto(photos[nextIndex]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [modalHotel, selectedPhoto]);

  const handleViewHotel = (hotel) => {
    trackVisitor('hotels', hotel.bid, 'Hotel listing clicked');
    setModalHotel(hotel);
  };

  // Build a Google Maps search URL from hotel address or location
  const buildMapUrl = (h) => {
    if (!h) return '';
    // Prefer lat/lon pin if available
    if (h.lat != null && h.lon != null) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(h.lat + ',' + h.lon)}`;
    }
    const q = h.address1 && h.address1.trim() !== '' ? h.address1 : `${h.locationname || ''} ${h.areaname || ''}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  };

  // Featured hotels logic, sorted by listorder/listingorder
  const featuredHotels = hotelsArray
    .filter(h => h.featuredlisting === true || h.featuredlisting === 1)
    .sort((a, b) => {
      const la = (typeof a.listorder === 'number' ? a.listorder : (typeof a.listingorder === 'number' ? a.listingorder : Number.MAX_SAFE_INTEGER));
      const lb = (typeof b.listorder === 'number' ? b.listorder : (typeof b.listingorder === 'number' ? b.listingorder : Number.MAX_SAFE_INTEGER));
      if (la !== lb) return la - lb;
      const da = a.dateposted ? new Date(a.dateposted).getTime() : 0;
      const db = b.dateposted ? new Date(b.dateposted).getTime() : 0;
      return da - db;
    });
  const [featuredScrollRef, setFeaturedScrollRef] = useState(null);
  const scrollFeaturedBy = (delta) => {
    if (featuredScrollRef) {
      featuredScrollRef.scrollBy({ left: delta, behavior: 'smooth' });
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: '100vw', margin: 0, padding: 10, boxSizing: 'border-box', overflowX: 'hidden' }}>
      {/* Header */}
      <header style={{ background: '#e8f5e9', color: '#388e3c', padding: '0.2rem', textAlign: 'center', borderRadius: 8, marginBottom: 2, marginTop:0 }}>
        <h1 style={{ margin: 0, marginTop: 0, marginBottom: 0, fontSize: '1.0rem', fontWeight: 700 }}>Hotels, Villas, Resorts & Service Apartments</h1>
      </header>

      {/* Featured Section */}
      {featuredHotels.length > 0 && (
        <div style={{ marginBottom: '0.2rem', position: 'relative' }}>
          {/* Left scroll button */}
          <button
            aria-label="Scroll featured left"
            onClick={() => scrollFeaturedBy(-250)}
            style={{
              position: 'absolute',
              top: '35%',
              transform: 'translateY(-50%)',
              left: 0,
              zIndex: 2,
              background: 'rgba(56, 142, 60, 0.9)',
              color: '#fff',
              border: 'none',
              borderRadius: 20,
              width: 28,
              height: 28,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18
            }}
          >
            ‹
          </button>
          {/* Featured scroll container */}
          <div
            ref={el => setFeaturedScrollRef(el)}
            style={{
              display: 'flex',
              gap: 4,
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              padding: '4px 44px',
              scrollSnapType: 'x mandatory',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}
          >
            {featuredHotels.map((hotel) => (
              <div
                key={hotel.id}
                onClick={() => setModalHotel(hotel)}
                style={{
                  minWidth: 130,
                  maxWidth: 145,
                  height: 180,
                  background: '#fff',
                  border: '2px solid #388e3c',
                  borderRadius: 12,
                  padding: 8,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  scrollSnapAlign: 'start',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                <img
                  src={resolveImageUrl(hotel.imagepath)}
                  alt={hotel.businessname}
                  style={{ width: 125, height: 80, objectFit: 'cover', borderRadius: 8, marginBottom: 0 }}
                  onError={e => { e.target.onerror = null; e.target.src = placeholderImg; }}
                />
                <h3 style={{ margin: 0, fontSize: 13, color: '#388e3c' }}>{hotel.businessname}</h3>
                {hotel.businesstype && <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>{hotel.businesstype}</div>}
                <div style={{ fontSize: 12, color: '#888', marginBottom: 2, fontWeight: 600 }}>
                  📍 {hotel.locationname} {hotel.areaname ? `• ${hotel.areaname}` : ''}
                </div>
                {hotel.contactperson && (
                  <div style={{ fontSize: 12, color: '#777', marginBottom: 4, marginTop: '-2px' }}>
                    Contact: <span style={{ fontWeight: 600, color: '#555' }}>{hotel.contactperson}</span>
                  </div>
                )}
                {/* Social media icons */}
                {(hotel.sm_instagram || hotel.sm_facebook || hotel.sm_youtube) && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    {hotel.sm_instagram && (
                      <a
                        href={`https://instagram.com/${hotel.sm_instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <InstagramIcon size={20} />
                      </a>
                    )}
                    {hotel.sm_facebook && (
                      <a
                        href={`https://facebook.com/${hotel.sm_facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <FacebookIcon size={20} />
                      </a>
                    )}
                    {hotel.sm_youtube && (
                      <a
                        href={`https://youtube.com/${hotel.sm_youtube}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <YouTubeIcon size={20} />
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Right scroll button */}
          <button
            aria-label="Scroll featured right"
            onClick={() => scrollFeaturedBy(250)}
            style={{
              position: 'absolute',
              top: '35%',
              transform: 'translateY(-50%)',
              right: 0,
              zIndex: 2,
              background: 'rgba(56, 142, 60, 0.9)',
              color: '#fff',
              border: 'none',
              borderRadius: 20,
              width: 28,
              height: 28,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18
            }}
          >
            ›
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0 0 0', flexWrap: 'wrap' }}>
        <select
          value={location}
          onChange={e => setLocation(e.target.value)}
          style={{ padding: 7, borderRadius: 6, border: '1px solid #90caf9', width: 120, boxSizing: 'border-box' }}
        >
          <option value="">All Locations</option>
          {locationOptions.map((loc, i) => (
            <option key={i} value={loc}>{loc}</option>
          ))}
        </select>
        <select
          value={businesstype}
          onChange={e => setBusinesstype(e.target.value)}
          style={{ padding: 7, borderRadius: 6, border: '1px solid #90caf9', width: 150, boxSizing: 'border-box' }}
        >
          <option value="">All Types</option>
          {businesstypeOptions.map((type, i) => (
            <option key={i} value={type}>{type}</option>
          ))}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', position: 'relative', flex: '0 1 auto', minWidth: 75, maxWidth: 300, gap: 8 }}>
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            style={{ padding: 7, borderRadius: 6, border: '1px solid #90caf9', width: '100%', boxSizing: 'border-box' }}
          />
          {showSuggestions && nameSuggestions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              width: '100%',
              background: '#fff',
              border: '1px solid #90caf9',
              borderRadius: 6,
              marginTop: 2,
              maxHeight: 200,
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {nameSuggestions.map((name, i) => (
                <div
                  key={i}
                  onClick={() => {
                    setSearch(name);
                    setShowSuggestions(false);
                  }}
                  style={{
                    padding: '8px 10px',
                    cursor: 'pointer',
                    borderBottom: i < nameSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}
                  onMouseEnter={e => e.target.style.background = '#e3f2fd'}
                  onMouseLeave={e => e.target.style.background = '#fff'}
                >
                  {name}
                </div>
              ))}
            </div>
          )}
          <span style={{ marginLeft: 8, fontSize: '0.70rem', color: '#1976d2', whiteSpace: 'nowrap' }}>
            Records listed: {filteredHotels.length}
          </span>
        </div>
        {(search || location) && (
          <button 
            onClick={() => { setSearch(''); setLocation(''); }} 
            style={{ 
              padding: '7px 14px', 
              borderRadius: 6, 
              border: 'none', 
              background: '#e3f2fd', 
              color: '#1976d2', 
              fontWeight: 600, 
              cursor: 'pointer', 
              whiteSpace: 'nowrap',
              fontSize: '14px'
            }}
          >
            Clear
          </button>
        )}
      </div>
      
      {/* Records listed message moved next to search box */}
      
      {/* Hotel list */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: 8, justifyContent: 'flex-start' }}>
        {filteredHotels.map((hotel, idx) => (
          <div
            key={hotel.id}
            style={{
              width: '100%',
              maxWidth: 340,
              background: '#e3f2fd',
              borderRadius: 10,
              boxShadow: '0 1px 4px #90caf9',
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'box-shadow 0.2s',
              boxSizing: 'border-box'
            }}
            onClick={() => setModalHotel(hotel)}
            onKeyDown={e => { if (e.key === 'Enter') setModalHotel(hotel); }}
            tabIndex={0}
            role="button"
            title={`View details for ${hotel.businessname}`}
          >
            <div style={{ width: '100%', maxWidth: 310, height: 170, position: 'relative', marginBottom: 10 }}>
              <img
                src={resolveImageUrl(hotel.imagepath)}
                alt={hotel.businessname}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 7 }}
                onError={e => { e.target.onerror = null; e.target.src = placeholderImg; }}
              />
              {(hotel.photos && hotel.photos.length > 1) && (
                <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '4px 8px', borderRadius: 12, fontSize: 12 }}>
                  {hotel.photos.length} photos
                </div>
              )}
              {hotel.featuredlisting && (
                <div style={{ position: 'absolute', top: 8, right: 8, background: '#1976d2', color: '#fff', padding: '4px 8px', borderRadius: 12, fontSize: 12 }}>
                  Featured
                </div>
              )}
            </div>
            <h3 className="hotel-name">{hotel.businessname}</h3>
            <p className="hotel-location">{hotel.locationname} - {hotel.areaname}</p>
            {hotel.title && <p style={{ margin: '3px 0', fontSize: 13, color: '#333', textAlign: 'center' }}>{hotel.title}</p>}
            {hotel.address1 && (
              <p style={{ margin: '3px 0', fontSize: 12, color: '#666', textAlign: 'center' }}>
                {hotel.address1}
                <a
                  href={buildMapUrl(hotel)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginLeft: 8, color: '#1976d2', textDecoration: 'none', fontWeight: 600, fontSize: 12 }}
                >
                  ▸ View on map
                </a>
              </p>
            )}
            {hotel.contactnumber && <p style={{ margin: '3px 0', fontSize: 13, color: '#1976d2', textAlign: 'center' }}>📞 {hotel.contactnumber}</p>}
            <p style={{ margin: '3px 0', fontSize: 16, fontWeight: 'bold', color: '#1976d2' }}>{hotel.price}</p>
            <p style={{ margin: '3px 0', fontSize: 14 }}>⭐ {hotel.rating}/5</p>
          </div>
        ))}
      </div>

      {/* Modal */}
      {modalHotel && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.6)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20
          }}
          onClick={() => setModalHotel(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: isMobile ? 12 : 18,
              maxWidth: isMobile ? '95vw' : 420,
              width: isMobile ? '95vw' : 'min(420px, 96%)',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative'
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setModalHotel(null)}
              aria-label="Close modal"
              title="Close"
              onMouseEnter={() => setHoverClose(true)}
              onMouseLeave={() => setHoverClose(false)}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: hoverClose ? 'rgba(25,118,210,0.95)' : 'rgba(255,255,255,0.92)',
                color: hoverClose ? '#fff' : '#333',
                border: hoverClose ? '1px solid rgba(25,118,210,0.12)' : '1px solid rgba(0,0,0,0.08)',
                borderRadius: '50%',
                width: 40,
                height: 40,
                padding: 6,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: hoverClose ? '0 6px 18px rgba(25,118,210,0.28)' : '0 2px 6px rgba(0,0,0,0.08)',
                transition: 'background 120ms ease, box-shadow 120ms ease, transform 120ms ease',
                zIndex: 30
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M18 6L6 18" stroke={hoverClose ? '#fff' : '#333'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M6 6L18 18" stroke={hoverClose ? '#fff' : '#333'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            
            {/* Main selected photo with prev/next controls */}
            <div style={{ position: 'relative', marginBottom: 3, width: '100%', height: isMobile ? 220 : 320, overflow: 'hidden', borderRadius: 8 }}>
              {(() => {
                const photos = (modalHotel.photos && modalHotel.photos.length > 0)
                  ? modalHotel.photos
                  : [];
                if (photos.length === 0) {
                  return (
                    <img
                      src={placeholderImg}
                      alt="No images available"
                      style={{ width: '100%', height: isMobile ? 220 : 320, objectFit: 'cover', display: 'block', borderRadius: 8 }}
                    />
                  );
                }
                return (
                  <>
                    <img
                      src={resolveImageUrl(selectedPhoto || photos[0])}
                      alt={modalHotel.businessname}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={e => { e.target.onerror = null; e.target.src = placeholderImg; }}
                    />
                    {/* Prev/Next buttons */}
                    {photos.length > 1 && (
                      <>
                        <button
                          aria-label="Previous image"
                          onClick={() => {
                            const idx = photos.findIndex(p => p === selectedPhoto);
                            const current = idx === -1 ? 0 : idx;
                            const prevIndex = (current - 1 + photos.length) % photos.length;
                            setSelectedPhoto(photos[prevIndex]);
                          }}
                          onMouseEnter={() => setHoverPrev(true)}
                          onMouseLeave={() => setHoverPrev(false)}
                          style={{
                            position: 'absolute',
                            left: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: hoverPrev ? 'rgba(25,118,210,0.95)' : 'rgba(0,0,0,0.5)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 20,
                            width: 44,
                            height: 44,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: hoverPrev ? '0 6px 18px rgba(25,118,210,0.3)' : '0 3px 8px rgba(0,0,0,0.25)',
                            transition: 'background 120ms ease, box-shadow 120ms ease, transform 120ms ease'
                          }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                        <button
                          aria-label="Next image"
                          onClick={() => {
                            const idx = photos.findIndex(p => p === selectedPhoto);
                            const current = idx === -1 ? 0 : idx;
                            const nextIndex = (current + 1) % photos.length;
                            setSelectedPhoto(photos[nextIndex]);
                          }}
                          onMouseEnter={() => setHoverNext(true)}
                          onMouseLeave={() => setHoverNext(false)}
                          style={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: hoverNext ? 'rgba(25,118,210,0.95)' : 'rgba(0,0,0,0.5)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 20,
                            width: 44,
                            height: 44,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: hoverNext ? '0 6px 18px rgba(25,118,210,0.3)' : '0 3px 8px rgba(0,0,0,0.25)',
                            transition: 'background 120ms ease, box-shadow 120ms ease, transform 120ms ease'
                          }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                            <path d="M9 6L15 12L9 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Thumbnails strip (scrollable horizontally) with scroll buttons */}
            {(() => {
              const photos = (modalHotel.photos && modalHotel.photos.length > 0)
                ? modalHotel.photos
                : [];
              return photos.length > 0 ? (
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <button
                    onClick={() => {
                      const container = document.getElementById('hotel-photo-scroll-container');
                      if (container) container.scrollBy({ left: -100, behavior: 'smooth' });
                    }}
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(0, 0, 0, 0.5)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      cursor: 'pointer',
                      zIndex: 2,
                    }}
                    aria-label="Scroll left"
                  >
                    ‹
                  </button>

                  <div
                    id="hotel-photo-scroll-container"
                    style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollBehavior: 'smooth', padding: '0 40px' }}
                  >
                    {photos.map((p, i) => (
                      <img
                        key={i}
                        src={resolveImageUrl(p)}
                        alt={`${modalHotel.businessname} ${i + 1}`}
                        onClick={() => setSelectedPhoto(p)}
                        onKeyDown={e => { if (e.key === 'Enter') setSelectedPhoto(p); }}
                        tabIndex={0}
                        style={{
                          width: isMobile ? 72 : 96,
                          height: isMobile ? 48 : 64,
                          objectFit: 'cover',
                          borderRadius: 6,
                          cursor: 'pointer',
                          border: selectedPhoto === p ? (isMobile ? '3px solid #1976d2' : '3px solid #1976d2') : '2px solid rgba(0,0,0,0.08)'
                        }}
                        onError={e => { e.target.onerror = null; e.target.src = placeholderImg; }}
                      />
                    ))}
                  </div>

                  <button
                    onClick={() => {
                      const container = document.getElementById('hotel-photo-scroll-container');
                      if (container) container.scrollBy({ left: 100, behavior: 'smooth' });
                    }}
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(0, 0, 0, 0.5)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      cursor: 'pointer',
                      zIndex: 2,
                    }}
                    aria-label="Scroll right"
                  >
                    ›
                  </button>
                </div>
              ) : null;
            })()}

            <h2 style={{ margin: '0 0 4px 0', color: '#1976d2', fontSize: 18 }}>{modalHotel.businessname}</h2>
            <p style={{ margin: '2px 0', fontSize: 15 }}><strong>Location:</strong> {modalHotel.locationname}-{modalHotel.areaname}</p>
            <p style={{ margin: '2px 0', fontSize: 15 }}><strong>Price:</strong> {modalHotel.price}</p>
            <p style={{ margin: '2px 0', fontSize: 15 }}><strong>Rating:</strong> ⭐ {modalHotel.rating}/5</p>
            
                  <div style={{ marginTop: 4, marginBottom: 4 }}>
                  <strong style={{ fontSize: '0.90rem', color: '#333', display: 'inline' }}>Description:</strong>
                  <span style={{ marginLeft: 6, color: '#333', lineHeight: 1.4, fontSize: '0.90rem', display: 'inline' }}>{modalHotel.aboutdescription}</span>
                </div>
                
            {/* Additional details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 2 }}>
              <div style={{ fontSize: 16, marginBottom: 1 }}><strong>Contact Details:</strong></div>
              {modalHotel.contactperson && (
                <div style={{ margin: '1px 0', fontSize: 14 }}><strong>Name:</strong> {modalHotel.contactperson}</div>
              )}
              {modalHotel.contactnumber && (
                <div style={{ margin: '1px 0', fontSize: 14 }}><strong>Number:</strong> <a href={`tel:${modalHotel.contactnumber}`} style={{ color: '#1976d2', textDecoration: 'none' }}>{modalHotel.contactnumber}</a></div>
              )}
              {modalHotel.emailid && (
                <div style={{ margin: '1px 0', fontSize: 14 }}><strong>Email:</strong> <a href={`mailto:${modalHotel.emailid}`} style={{ color: '#1976d2', textDecoration: 'none' }}>{modalHotel.emailid}</a></div>
              )}
                {modalHotel.address1 && (
                  <div style={{ margin: '1px 0', fontSize: 14 }}>
                    <strong>Address:</strong> {modalHotel.address1}
                    <a
                      href={buildMapUrl(modalHotel)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ marginLeft: 6, color: '#1976d2', textDecoration: 'none', fontWeight: 600 }}
                    >
                      ▸ View on map
                    </a>
                  </div>
                )}
                {modalHotel.website && (
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#666', marginTop: 4, marginBottom: 12 }}>
                    Website: {modalHotel.website ? (
                      <a
                        href={modalHotel.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontWeight: 400, color: '#1976d2', textDecoration: 'underline', wordBreak: 'break-all' }}
                      >
                        {modalHotel.website}
                      </a>
                    ) : (
                      <span style={{ fontWeight: 400, color: '#555' }}>{modalHotel.website}</span>
                    )}
                  </div>
                )}
              {/* Amenities chips */}
              {modalHotel.amenities && modalHotel.amenities.length > 0 && (
                <div style={{ marginTop: 5 }}>
                  <strong>Amenities:</strong>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                    {modalHotel.amenities.map((a, i) => (
                      <span key={i} style={{ background: '#e3f2fd', color: '#0d47a1', padding: '6px 10px', borderRadius: 18, fontSize: 13 }}>{a}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {modalHotel.urllink && (
              <a 
                href={modalHotel.urllink} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  marginTop: 10,
                  padding: '4px 10px',
                  background: '#1976d2',
                  color: '#fff',
                  textDecoration: 'none',
                  borderRadius: 6,
                  fontWeight: 600
                }}
              >
                Visit Website
              </a>
            )}
            

            {(modalHotel.sm_instagram || modalHotel.sm_facebook || modalHotel.sm_youtube || modalHotel.sm_twitter || modalHotel.sm_linkedin) && (
              <div style={{ marginTop: 16, paddingTop: 6, borderTop: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ fontSize: 14, color: '#333' }}>Follow us:</strong>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {modalHotel.sm_instagram && (
                      <a
                        href={`https://instagram.com/${modalHotel.sm_instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 30,
                          height: 30,
                          borderRadius: 6,
                          background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                          color: '#fff',
                          fontSize: 16,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        title="Instagram"
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <svg width="18" height="18" viewBox="0 0 448 512" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9 114.9-51.3 114.9-114.9S287.7 141 224.1 141zm0 186c-39.5 0-71.5-32-71.5-71.5s32-71.5 71.5-71.5 71.5 32 71.5 71.5-32 71.5-71.5 71.5zm146.4-194.3c0 14.9-12 26.9-26.9 26.9s-26.9-12-26.9-26.9 12-26.9 26.9-26.9 26.9 12 26.9 26.9zm76.1 27.2c-1.7-35.3-9.9-66.7-36.2-92.1S388.6 7.6 353.3 5.9C317.7 4.2 130.3 4.2 94.7 5.9 59.4 7.6 28 15.8 2.7 41.1S7.6 123.4 5.9 158.7C4.2 194.3 4.2 381.7 5.9 417.3c1.7 35.3 9.9 66.7 36.2 92.1s56.8 34.5 92.1 36.2c35.6 1.7 223 1.7 258.6 0 35.3-1.7 66.7-9.9 92.1-36.2s34.5-56.8 36.2-92.1c1.7-35.6 1.7-223 0-258.6zm-48.5 292c-7.8 19.6-22.9 34.7-42.5 42.5-29.4 11.7-99.2 9-132.5 9s-103.1 2.6-132.5-9c-19.6-7.8-34.7-22.9-42.5-42.5-11.7-29.4-9-99.2-9-132.5s-2.6-103.1 9-132.5c7.8-19.6 22.9-34.7 42.5-42.5 29.4-11.7 99.2-9 132.5-9s103.1-2.6 132.5 9c19.6 7.8 34.7 22.9 42.5 42.5 11.7 29.4 9 99.2 9 132.5s2.6 103.1-9 132.5z"/></svg>
                      </a>
                    )}
                    {modalHotel.sm_facebook && (
                      <a
                        href={`https://facebook.com/${modalHotel.sm_facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 30,
                          height: 30,
                          borderRadius: 6,
                          background: '#1877F2',
                          color: '#fff',
                          fontSize: 16,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        title="Facebook"
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <svg width="18" height="18" viewBox="0 0 320 512" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M279.14 288l14.22-92.66h-88.91V127.91c0-25.35 12.42-50.06 52.24-50.06H293V6.26S259.5 0 225.36 0c-73.22 0-121 44.38-121 124.72v70.62H22.89V288h81.47v224h100.2V288z"/></svg>
                      </a>
                    )}
                    {modalHotel.sm_youtube && (
                      <a
                        href={`https://youtube.com/${modalHotel.sm_youtube}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 30,
                          height: 30,
                          borderRadius: 6,
                          background: '#FF0000',
                          color: '#fff',
                          fontSize: 16,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        title="YouTube"
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <svg width="18" height="18" viewBox="0 0 576 512" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M549.655 124.083c-6.281-23.65-24.836-42.205-48.486-48.486C465.454 64 288 64 288 64s-177.454 0-213.169 11.597c-23.65 6.281-42.205 24.836-48.486 48.486C16.748 159.798 16.748 256 16.748 256s0 96.202 9.597 131.917c6.281 23.65 24.836 42.205 48.486 48.486C110.546 448 288 448 288 448s177.454 0 213.169-11.597c23.65-6.281 42.205-24.836 48.486-48.486C559.252 352.202 559.252 256 559.252 256s0-96.202-9.597-131.917zM232.1 338.1V173.9l142.7 82.1-142.7 82.1z"/></svg>
                      </a>
                    )}
                    {modalHotel.sm_twitter && (
                      <a
                        href={`https://twitter.com/${modalHotel.sm_twitter}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 30,
                          height: 30,
                          borderRadius: 6,
                          background: '#1DA1F2',
                          color: '#fff',
                          fontSize: 16,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        title="Twitter"
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <svg width="18" height="18" viewBox="0 0 512 512" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M459.4 151.7c.32 4.54.32 9.1.32 13.66 0 138.72-105.58 298.56-298.56 298.56-59.5 0-114.68-17.22-161.14-47.11 8.44.97 16.87 1.44 25.6 1.44 49.06 0 94.19-16.63 132.14-44.84-46.07-.86-84.8-31.18-98.1-72.77 6.5.97 13.01 1.94 19.84 1.94 9.42 0 18.84-1.3 27.61-3.63-48.08-9.7-84.12-51.98-84.12-102.98v-1.3c14.08 7.83 30.18 12.67 47.24 13.32-28.26-18.84-46.78-51.01-46.78-87.39 0-19.49 5.2-37.36 14.08-52.95 51.34 62.76 128.3 103.98 214.7 108.46-1.62-7.79-2.6-15.91-2.6-24.03 0-58.72 47.58-106.3 106.3-106.3 30.63 0 58.26 12.67 77.61 33.14 24.26-4.54 47.24-13.66 67.72-25.84-7.97 24.9-24.9 45.81-46.78 59.06 21.58-2.27 42.29-8.32 61.32-16.9-14.08 21.26-31.81 39.97-52.42 54.82z"/></svg>
                      </a>
                    )}
                    {modalHotel.sm_linkedin && (
                      <a
                        href={`https://linkedin.com/in/${modalHotel.sm_linkedin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 30,
                          height: 30,
                          borderRadius: 6,
                          background: '#0077B5',
                          color: '#fff',
                          fontSize: 16,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        title="LinkedIn"
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <svg width="18" height="18" viewBox="0 0 448 512" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M100.28 448H7.4V148.9h92.88zm-46.44-340.7C24.09 107.3 0 83.2 0 53.6A53.6 53.6 0 0 1 53.6 0a53.6 53.6 0 0 1 53.6 53.6c0 29.6-24.09 53.7-53.36 53.7zM447.8 448h-92.4V302.4c0-34.7-12.4-58.4-43.3-58.4-23.6 0-37.6 15.9-43.7 31.3-2.3 5.6-2.8 13.4-2.8 21.2V448h-92.4s1.2-241.1 0-266.1h92.4v37.7c12.3-19 34.3-46.1 83.5-46.1 60.9 0 106.6 39.7 106.6 125.1V448z"/></svg>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <button
              onClick={() => handleShareClick(modalHotel)}
              style={{
                marginTop: 16,
                padding: '8px 10px',
                background: '#4CAF50',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              📤 Share
            </button>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#323232',
          color: '#fff',
          padding: '12px 24px',
          borderRadius: 6,
          zIndex: 10000,
          fontSize: 14
        }}>
          Link copied to clipboard!
        </div>
      )}
    </div>
  );
};

export default Hotels;